import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Smartphone } from "lucide-react";
import { providers, usps as mobielUsps } from "@/data/mobiel";
import logoVodafone from "@/assets/logo-vodafone.png";
import mobielHeroBg from "@/assets/mobiel-hero-bg.jpg";
import mobielHeroVideo from "@/assets/mobiel-hero-video.mp4";

const provider = providers.find((p) => p.slug === "vodafone")!;

const MobielVodafonePage = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[40vh] flex items-center overflow-hidden">
        <video autoPlay muted loop playsInline poster={mobielHeroBg} className="absolute inset-0 h-full w-full object-cover">
          <source src={mobielHeroVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${provider.kleur}cc, ${provider.kleur}88 50%, rgba(0,0,0,0.4))` }} />
        <div className="absolute right-[-4%] top-1/2 -translate-y-1/2 pointer-events-none select-none opacity-[0.08]">
          <img loading="eager" decoding="async" fetchPriority="high" src={logoVodafone} alt="" className="w-[340px] md:w-[480px] h-auto rotate-[-12deg] brightness-0 invert" />
        </div>
        <div className="container relative z-10 py-16">
          <Link to="/diensten/mobiel" className="inline-flex items-center text-white/80 hover:text-white mb-6 text-sm transition-colors">
            <ArrowLeft className="mr-2 h-4 w-4" /> Terug naar overzicht
          </Link>
          <motion.div
            className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white p-2.5 shadow-lg"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <img loading="lazy" decoding="async" src={logoVodafone} alt="Vodafone logo" className="w-full h-full object-contain" />
          </motion.div>
          <motion.h1 className="text-3xl md:text-5xl font-bold text-white" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {provider.naam} Zakelijk Mobiel
          </motion.h1>
          <motion.p className="text-white/80 mt-2 max-w-lg" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            {provider.beschrijving}
          </motion.p>
          <motion.div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            {mobielUsps.map((usp, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${i === 0 ? "bg-primary" : i === 1 ? "bg-rinqel-blue" : i === 2 ? "bg-rinqel-green" : "bg-primary"}`} />
                <span className="text-white/90 text-sm font-medium">{usp.titel}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Abonnementen */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl" style={{ backgroundColor: `${provider.kleur}15` }}>
                <Smartphone className="h-5 w-5" style={{ color: provider.kleur }} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">Sim Only — Mobiel</h2>
            </div>
            <p className="text-muted-foreground mb-8 ml-[52px]">Bellen, sms'en en internetten op je smartphone · Maandelijks opzegbaar</p>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {provider.abonnementen.map((abo, i) => (
                <motion.div key={abo.naam} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                  <Card className={`h-full relative overflow-hidden transition-shadow hover:shadow-lg ${abo.populair ? 'ring-2 ring-offset-2' : ''}`} style={{ borderColor: provider.kleur, boxShadow: `0 0 0 2px ${provider.kleur}`, ...(abo.populair ? { ['--tw-ring-color' as string]: provider.kleur } : {}) }}>
                    {abo.populair && (
                      <div className="absolute top-0 left-0 right-0 text-center text-[10px] font-bold uppercase tracking-wider text-white py-0.5" style={{ backgroundColor: provider.kleur }}>
                        ⭐ Meest gekozen
                      </div>
                    )}
                    <div className={`${abo.populair ? 'mt-5' : ''} absolute top-0 left-0 right-0 text-center text-xs font-semibold text-white py-1 flex items-center justify-center gap-1.5`} style={{ backgroundColor: provider.kleur }}>
                      <Smartphone className="h-3 w-3" />
                      {abo.naam}
                    </div>
                    <CardContent className={`p-6 flex flex-col items-center text-center ${abo.populair ? 'pt-14' : 'pt-10'} h-full`}>
                      <div className="text-3xl font-bold mb-1" style={{ color: provider.kleur }}>{abo.prijs}</div>
                      <span className="text-xs text-muted-foreground mb-6">per maand</span>
                      <div className="w-full space-y-3 text-sm text-left flex-1">
                        <div className="flex items-center gap-2">
                          <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                          <span><strong>{abo.data}</strong> data</span>
                        </div>
                        {abo.kenmerken?.map((kenmerk, ki) => (
                          <div key={ki} className="flex items-center gap-2">
                            <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                            <span>{kenmerk}</span>
                          </div>
                        )) || (
                          <>
                            <div className="flex items-center gap-2">
                              <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                              <span><strong>{abo.minuten}</strong> bellen & sms</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                              <span>EU-roaming inbegrepen</span>
                            </div>
                          </>
                        )}
                      </div>
                      <Button asChild className="w-full mt-6" style={{ backgroundColor: provider.kleur, color: 'white' }}>
                        <TrackedLink to="/contact" trackLabel="Aanvragen" trackLocation="mobiel-vodafone-bundel">Aanvragen</TrackedLink>
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Extra bundels */}
      {provider.extraBundels.length > 0 && (
        <section className="py-16 bg-muted/30">
          <div className="container">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Extra bundels & opties</h2>
            <p className="text-muted-foreground mb-10">Voeg extra's toe aan je abonnement, ook maandelijks opzegbaar.</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {provider.extraBundels.map((bundel, i) => (
                <motion.div key={bundel.naam} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
                  <Card className="h-full hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold mb-1">{bundel.naam}</h3>
                          <p className="text-sm text-muted-foreground">{bundel.beschrijving}</p>
                        </div>
                        <Badge variant="secondary" className="shrink-0 font-semibold">{bundel.prijs}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16" style={{ background: `linear-gradient(135deg, ${provider.kleur}, ${provider.kleur}cc)` }}>
        <div className="container text-center text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Direct aan de slag met {provider.naam}?</h2>
          <p className="text-white/80 mb-8 max-w-lg mx-auto">Neem contact op en we regelen alles voor je. Geen aansluitkosten, geen gedoe.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="hero-outline" className="border-white text-white hover:bg-white/10">
              <TrackedLink to="/contact" trackLabel="Neem contact op" trackLocation="mobiel-vodafone-cta">Neem contact op <ArrowRight className="ml-2 h-4 w-4" /></TrackedLink>
            </Button>
            <Button asChild size="lg" variant="ghost" className="text-white hover:bg-white/10">
              <Link to="/diensten/mobiel"><ArrowLeft className="mr-2 h-4 w-4" /> Alle netwerken bekijken</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default MobielVodafonePage;
